/**
 * A one-month calendar that picks a range of days. Days are local "YYYY-MM-DD"
 * strings, so they compare as text. The first click sets both ends to that
 * day (a one-day range); the next click sets the other end, which may be the
 * same day again (or the start, when it is earlier). Days
 * after `max` cannot be picked. Sized to sit beside two time boxes in a dialog.
 */

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const pad = (n: number) => String(n).padStart(2, "0");
const dayKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** The weeks shown for the month holding `month` (weeks start on Monday). */
function grid(month: Date): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const lead = (first.getDay() + 6) % 7;
  const days = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const weeks = Math.ceil((lead + days) / 7);
  return Array.from(
    { length: weeks * 7 },
    (_, i) => new Date(first.getFullYear(), first.getMonth(), i - lead + 1),
  );
}

export function RangeCalendar({
  start,
  end,
  max,
  onChange,
  label = "Dates",
}: {
  start: string;
  end: string;
  /** The last day that can be picked. */
  max: string;
  onChange: (start: string, end: string) => void;
  label?: string;
}) {
  const [month, setMonth] = useState(() => new Date(`${end || max}T00:00`));
  // After a first click the next one closes the range around it.
  const [anchor, setAnchor] = useState<string | null>(null);
  const shift = (by: number) => setMonth(new Date(month.getFullYear(), month.getMonth() + by, 1));
  const pick = (day: string) => {
    if (!anchor) return (setAnchor(day), onChange(day, day));
    setAnchor(null);
    onChange(day < anchor ? day : anchor, day < anchor ? anchor : day);
  };
  const title = month.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const nextMonth = dayKey(new Date(month.getFullYear(), month.getMonth() + 1, 1));

  return (
    <div className="flex w-[224px] shrink-0 flex-col gap-1.5" role="group" aria-label={label}>
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => shift(-1)}
          className="btn btn-ghost btn-icon btn-sm"
        >
          <ChevronLeft className="size-4" />
        </button>
        <span className="text-[13px] font-semibold">{title}</span>
        <button
          type="button"
          aria-label="Next month"
          disabled={nextMonth > max}
          onClick={() => shift(1)}
          className="btn btn-ghost btn-icon btn-sm"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 text-center text-[11px] text-faint">
        {WEEKDAYS.map((w) => (
          <span key={w} className="py-1">
            {w}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-0.5">
        {grid(month).map((d) => {
          const day = dayKey(d);
          const inMonth = d.getMonth() === month.getMonth();
          const edge = day === start || day === end;
          const inside = day > start && day < end;
          return (
            <button
              key={day}
              type="button"
              disabled={day > max}
              aria-pressed={edge || inside}
              aria-label={d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
              onClick={() => pick(day)}
              className={`h-7 text-xs tabular-nums transition-colors disabled:cursor-not-allowed disabled:opacity-30 ${
                edge
                  ? "rounded-md bg-primary font-semibold text-on-primary"
                  : inside
                    ? "bg-run-bg text-ink"
                    : `rounded-md hover:bg-hover ${inMonth ? "text-body" : "text-dim"}`
              } ${day === max && !edge ? "font-semibold underline decoration-link underline-offset-4" : ""}`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
      {anchor && <p className="text-[11px] text-link">Now pick the last day, or the same day again.</p>}
    </div>
  );
}

export { dayKey };
